import { Link } from "react-router-dom";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const ORGANIZERS = [
  {
    id: 1,
    name: "Justyna Trzaskowska",
    role: "",
    bio: "Zawsze bliscy byli mi ludzie, relacje i rozwój. Ukończyłam pedagogikę na Uniwersytecie Warszawskim, specjalizując się w kształceniu dorosłych. Już podczas studiów z radością tworzyłam własną szkołę językową — przestrzeń, w której zarówno dzieci, jak i dorośli mogli korzystać z mojej wiedzy i doświadczenia.\n\nJednak życie przyniosło mi rolę, która stała się dla mnie najważniejsza — rolę mamy.\n\nKiedy urodziła się moja pierwsza córka, wiedziałam, że chcę być blisko niej — uczestniczyć w jej dorastaniu, towarzyszyć jej na co dzień i być obok w tych małych i wielkich momentach. Pojawienie się kolejnej trójki dzieci tylko umocniło mnie w przekonaniu, że rodzina i budowanie domu są tym, czemu chcę poświęcić dużą część swojego życia.\n\nŻeby być w domu i pomagać tym którzy są przy mnie — przez cały czas się kształcę. Dzięki nieustannemu pogłębianiu kompetencji pedagoga i mamy tworzę przestrzenie rozwoju dla kobiet, które pragną zmiany. Rozwijam też swoje pasje, dbam o siebie żeby lepiej dbać o moich bliskich.\n\nOd 20 lat razem z mężem tworzymy dom pełen ciepła, radości, rozmów i śmiechu, a także — jak to w dużej rodzinie — całkiem sporej dawki codziennego chaosu.\n\nDziś chcę dzielić się przede wszystkim tym, czego nauczyło mnie życie — refleksjami o kobiecości, rodzinie, relacjach, wyborach i odwadze, by budować życie, które naprawdę jest nasze.",
  },
  {
    id: 2,
    name: "Anna Bosak",
    role: "",
    bio: "Jestem szczęśliwą żoną i mamą czwórki dzieci – Huberta, Piotrka, Karola i Julki. Po urodzeniu pierwszego dziecka na dłuższy czas zawiesiłam pracę poza domem, chociaż moja ścieżka edukacji i pracy nie zmierzała w tym kierunku. Ukończyłam studia magisterskie na Uniwersytecie Ekonomicznym w Poznaniu i doktoranckie na SGH w Warszawie.\n\nPracowałam w agencji reklamowej, zajmowałam się komunikacją i marketingiem. Jednak później odkryłam jak ważna przy małych dzieciach jest moja obecność i zaangażowanie w domu. I uświadamiałam sobie jak bardzo mnie to rozwija, daje poczucie satysfakcji i spełnienia.\n\nZdobytą wcześniej wiedzę i doświadczenie zawodowe wykorzystywałam w pracy na rzecz mojej rodziny. Zafascynowana tym jak wiele jest podobieństw między pracą w domu a zawodową i jak dobrze to się sprawdza założyłam w 2015 roku bloga Notatki Zarządu. Chciałam dzielić się swoimi pomysłami i wspierać kobiety w wyzwaniach związanych z macierzyństwem. Prowadziłam też szkolenia przekazując konkretne wskazówki i pomagając rozwijać praktyczne umiejętności.\n\nPo kilku latach zdecydowałam się na kolejny krok – założenie Fundacji Priorytety, która dzięki swoim działaniom ma docierać nie tylko do wybranych mam, ale budować w świadomości społeczeństwa wartość pracy w domu i kobiety, która ją wykonuje.",
  },
];

// Single shared photo of both organizers. Leave empty to show placeholder.
const GROUP_PHOTO = "https://customer-assets-cm19k8pv.emergentagent.net/job_mama-congress/artifacts/67bfeccb1a865bf3_Screenshot%202026-10-14%20at%2023.08.46.png";

export default function Poznajmy() {
  useDocumentTitle("Poznajmy się", "Poznaj organizatorki mamy!kongres oraz Fundację Priorytety.");
  return (
    <div>
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-8 pb-14 relative">
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[#E6007A]/15 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -left-20 w-64 h-64 rounded-full bg-[#F48DBF]/25 blur-3xl pointer-events-none" />
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-6 relative">
          Poznajmy się
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] relative">
          Dwie mamy, <br />
          jeden <span className="italic text-[#E6007A]">pomysł</span>.
        </h1>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        {/* SINGLE PHOTO FRAME */}
        <div
          className="rounded-[48px] overflow-hidden bg-[#F4EFE6] aspect-[3/2] max-w-4xl mx-auto mb-12"
          data-testid="organizers-photo"
        >
          {GROUP_PHOTO ? (
            <img
              src={GROUP_PHOTO}
              alt="Organizatorki Mamy Kongres"
              className="w-[calc(100%+8px)] h-full object-cover"
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-center px-6">
              <div className="text-sm uppercase tracking-[0.3em] text-[#5C534D]">
                Miejsce na wspólne zdjęcie
              </div>
            </div>
          )}
        </div>

        {/* CAPTIONS FOR BOTH PEOPLE */}
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {ORGANIZERS.map((o, idx) => {
            return (
              <article
                key={o.id}
                data-testid={`organizer-${o.id}`}
                className="relative rounded-[32px] bg-[#FDDAE3] border border-[#F4B7CA] p-8 md:p-10 shadow-[0_10px_30px_rgba(230,0,122,0.06)] transition-transform duration-300 hover:-translate-y-1"
              >
                {/* Decorative pink corner blob */}
                <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-[#E6007A]/15 blur-2xl pointer-events-none" />

                <div className="relative mb-5">
                  <div className="font-display text-3xl md:text-4xl leading-tight">
                    {o.name}
                    <span className="block w-16 h-[3px] bg-[#E6007A] rounded-full mt-2" />
                  </div>
                </div>

                <div className="text-[#3d3733] leading-relaxed space-y-3 relative">
                  {o.bio.split(/\n+/).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* FUNDACJA PRIORYTETY */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24" data-testid="fundacja-info">
        <div className="rounded-[40px] bg-[#FDDAE3] border border-[#F4B7CA] p-8 md:p-12 shadow-[0_10px_30px_rgba(230,0,122,0.06)]">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-4">
            Fundacja Priorytety
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-6">
            Kim jesteśmy?
          </h2>
          <div className="text-[#5C534D] leading-relaxed space-y-4 max-w-3xl">
            <p>
              Fundacja Priorytety powstała, żeby upowszechniać wiedzę o wartości zaangażowania rodzinnego,
              budowania jasnego i radosnego domu. Dom jest miejscem, w którym człowiek zdobywa najważniejsze
              umiejętności, kształtuje charakter, poczucie własnej wartości, uczy się odróżniać dobro od zła.
              Zadbany, pełen miłości, mądrze prowadzony dom ma ogromny wpływ na rozwój każdej osoby i całego
              społeczeństwa.
            </p>
            <p>
              Kobietom w dzisiejszych czasach często jest bardzo trudno pogodzić wyzwania związane z pracą
              zawodową, obowiązkami domowymi i rodzinnymi. Zapytani o receptę na sukces życiowy Polacy w
              największym stopniu upatrują go w posiadaniu szczęśliwej, kochającej się rodziny, posiadaniu
              dzieci oraz możliwości umiejętnego łączenia szczęścia rodzinnego z życiem zawodowym.
              Tworzenie narzędzi pomagających zrealizować ten cel jest naszym priorytetem.
            </p>
          </div>

          <a
            href="https://fundacjapriorytety.pl"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fundacja-cta"
            className="mt-10 inline-flex items-center gap-5 group"
          >
            <img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_mama-congress/artifacts/dkg5rujs_image.png"
              alt="Fundacja Priorytety"
              className="h-14 md:h-16 w-auto transition-transform duration-300 group-hover:-translate-y-0.5"
            />
            <span className="font-semibold text-[#E6007A] link-underline">
              dowiedz się więcej
            </span>
          </a>
        </div>
      </section>

      <section className="bg-[#F4EFE6] py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl mb-6">
            Chcesz nas o coś zapytać?
          </h2>
          <Link
            to="/kontakt"
            data-testid="poznajmy-cta-contact"
            className="btn-lift inline-flex items-center rounded-full bg-[#E6007A] px-8 py-4 text-white font-semibold"
          >
            Napisz do nas
          </Link>
        </div>
      </section>
    </div>
  );
}
